import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Layout from "../components/Layout";
import SeoHead from "../components/SeoHead";
import { useAuth } from "../context/AuthContext";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [blocked, setBlocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) {
      const inbox = location.state?.from
        ? new URLSearchParams(location.state.from.search || "").get("inbox")
        : null;
      navigate(inbox ? `/dashboard?inbox=${inbox}` : "/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBlocked(false);
    setSubmitting(true);

    const result = await login({ email, password });
    setSubmitting(false);

    if (!result.ok) {
      setBlocked(!!result.blocked);
      setError(result.error);
      return;
    }

    const from = location.state?.from;
    const inbox = new URLSearchParams(from?.search || "").get("inbox");
    if (from?.pathname === "/dashboard" || inbox) {
      navigate(inbox ? `/dashboard?inbox=${inbox}` : "/dashboard", { replace: true });
    } else {
      navigate("/dashboard", { replace: true });
    }
  };

  return (
    <Layout>
      <SeoHead
        title="Login – Ghost Inbox"
        description="Sign in to your Ghost Inbox account to access your private temporary inbox dashboard."
        path="/login"
        noindex
      />
      <div className="mx-auto flex max-w-md flex-1 flex-col justify-center px-6 py-16">
        <h1 className="text-2xl font-bold text-white">Welcome back</h1>
        <p className="mt-2 text-sm text-zinc-400">Sign in to access your temporary inbox.</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-400">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="block text-xs font-medium text-zinc-400">Password</label>
              <Link to="/forgot-password" className="text-xs text-emerald-400 hover:underline">
                Forgot password?
              </Link>
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none"
            />
          </div>
          {error && (
            blocked ? (
              <p className="text-sm text-red-400">
                Please contact Admin from{" "}
                <Link to="/contact" className="text-emerald-400 hover:underline">
                  ghostinbox.xyz/contact
                </Link>{" "}
                page to unblock your account
              </p>
            ) : (
              <p className="text-sm text-red-400">{error}</p>
            )
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-emerald-500 py-3 text-sm font-semibold text-zinc-950 hover:bg-emerald-400 disabled:opacity-60"
          >
            {submitting ? "Signing in…" : "Login"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-zinc-500">
          Don&apos;t have an account?{" "}
          <Link to="/signup" className="text-emerald-400 hover:underline">
            Sign up
          </Link>
        </p>
      </div>
    </Layout>
  );
}

export default Login;
